import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Heart, Shield, Leaf, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { mockTestimonials } from '../mock';
import { fetchKits } from '../api';

const WHATSAPP_NUMBER = '918279765078';

const Hero = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const titleVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.3
      }
    }
  };

  const letterVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', damping: 12, stiffness: 200 }
    }
  };

  const title = "Ancient Wisdom, Modern Wellness";

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-background via-accent/20 to-background">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0],
            opacity: [0.1, 0.2, 0.1]
          }}
          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          className="absolute top-1/4 -left-20 w-96 h-96 bg-primary/20 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [90, 0, 90],
            opacity: [0.15, 0.25, 0.15]
          }}
          transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
          className="absolute bottom-1/4 -right-20 w-96 h-96 bg-secondary/20 rounded-full blur-3xl"
        />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-32 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full"
            >
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">Holistic Healing Since 1995</span>
            </motion.div>

            <motion.h1
              variants={titleVariants}
              initial="hidden"
              animate={isLoaded ? "visible" : "hidden"}
              className="text-5xl sm:text-6xl lg:text-7xl font-bold font-serif leading-tight"
            >
              {title.split(' ').map((word, wordIndex) => (
                <span key={wordIndex} className="inline-block">
                  {word.split('').map((letter, letterIndex) => (
                    <motion.span
                      key={`${wordIndex}-${letterIndex}`}
                      variants={letterVariants}
                      className="inline-block"
                    >
                      {letter}
                    </motion.span>
                  ))}
                  <span className="inline-block">&nbsp;</span>
                </span>
              ))}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.6 }}
              className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-xl"
            >
              Experience authentic Ayurvedic treatments, premium herbal medicines, and personalized consultations with certified practitioners.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button
                size="lg"
                className="bg-green-500 hover:bg-green-600 text-white group w-full sm:w-auto"
                onClick={() => window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hello Aatreya Herbals! I want to book a FREE consultation with a doctor.')}`, '_blank')}
                data-testid="hero-book-free-consultation-whatsapp"
              >
                <MessageCircle className="mr-2 w-5 h-5" />
                Book Free Consultation
              </Button>
              <Link to="/medicines">
                <Button size="lg" variant="outline" className="w-full sm:w-auto">
                  Explore Medicines
                </Button>
              </Link>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.6, duration: 0.6 }}
              className="grid grid-cols-3 gap-8 pt-8 border-t border-border"
            >
              {[
                { value: '10K+', label: 'Happy Patients' },
                { value: '50+', label: 'Herbal Products' },
                { value: '15+', label: 'Expert Practitioners' }
              ].map((stat, index) => (
                <div key={index}>
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 1.8 + index * 0.1, type: 'spring' }}
                    className="text-3xl font-bold font-serif text-primary"
                  >
                    {stat.value}
                  </motion.div>
                  <div className="text-sm text-muted-foreground mt-1">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Content - Image Grid */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="relative hidden lg:block"
          >
            <div className="grid grid-cols-2 gap-4">
              <motion.div
                whileHover={{ scale: 1.05, rotate: 2 }}
                className="relative h-64 rounded-2xl overflow-hidden shadow-2xl"
              >
                <img
                  src="https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=400&h=400&fit=crop"
                  alt="Ayurvedic herbs"
                  className="w-full h-full object-cover"
                />
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05, rotate: -2 }}
                className="relative h-64 rounded-2xl overflow-hidden shadow-2xl mt-12"
              >
                <img
                  src="https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=400&h=400&fit=crop"
                  alt="Ayurvedic treatment"
                  className="w-full h-full object-cover"
                />
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05, rotate: -2 }}
                className="relative h-64 rounded-2xl overflow-hidden shadow-2xl -mt-8"
              >
                <img
                  src="https://images.unsplash.com/photo-1471864190281-a93a3070b6de?w=400&h=400&fit=crop"
                  alt="Herbal medicine"
                  className="w-full h-full object-cover"
                />
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05, rotate: 2 }}
                className="relative h-64 rounded-2xl overflow-hidden shadow-2xl mt-4"
              >
                <img
                  src="https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400&h=400&fit=crop"
                  alt="Ayurvedic consultation"
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </div>

            {/* Floating Badge */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-full p-6 shadow-2xl"
            >
              <Leaf className="w-12 h-12 text-primary" />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const AnimatedSection = ({ children, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start('visible');
    }
  }, [isInView, controls]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 50 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const Home = () => {
  const [featuredMedicines, setFeaturedMedicines] = useState([]);

  useEffect(() => {
    fetchKits({ featured: true })
      .then((data) => setFeaturedMedicines(data.slice(0, 4)))
      .catch(() => setFeaturedMedicines([]));
  }, []);

  return (
    <div className="grain-texture">
      <Hero />

      {/* Featured Medicines */}
      <AnimatedSection className="py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 space-y-4">
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              className="text-primary font-medium text-sm tracking-wider uppercase"
            >
              Premium Collection
            </motion.span>
            <h2 className="text-4xl sm:text-5xl font-bold font-serif">Featured Health Kits</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Complete Ayurvedic solutions for specific health conditions
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {featuredMedicines.map((medicine, index) => (
              <motion.div
                key={medicine.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8 }}
                className="group"
              >
                <div
                  onClick={() => window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`🌿 *Order Request - Aatreya Herbals*\n\nI want to order:\n📦 *${medicine.name}*\n💰 Price: ₹${medicine.price}\n\nPlease share the ordering details.`)}`, '_blank')}
                  className="cursor-pointer"
                  data-testid={`home-kit-whatsapp-${medicine.id}`}
                >
                  <div className="bg-card rounded-2xl overflow-hidden border border-border shadow-lg hover:shadow-2xl transition-all duration-500">
                    <div className="relative h-56 overflow-hidden">
                      <motion.img
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.6 }}
                        src={medicine.image}
                        alt={`${medicine.name} - Ayurvedic ${medicine.category} health kit`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-4 right-4 bg-secondary text-secondary-foreground px-3 py-1 rounded-full text-xs font-medium">
                        {medicine.category}
                      </div>
                    </div>
                    <div className="p-6 space-y-3">
                      <h3 className="font-serif font-semibold text-xl group-hover:text-primary transition-colors">
                        {medicine.name}
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {medicine.description}
                      </p>
                      <div className="text-xs text-muted-foreground">
                        📦 {medicine.productsIncluded?.length || 4} products
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {medicine.benefits.slice(0, 2).map((benefit, i) => (
                          <span key={i} className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                            {benefit}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center justify-between pt-3">
                        <div className="flex flex-col">
                          {medicine.originalPrice && (
                            <span className="text-sm text-muted-foreground line-through">₹{medicine.originalPrice}</span>
                          )}
                          <span className="text-2xl font-bold text-primary">₹{medicine.price}</span>
                        </div>
                        <Button size="sm" className="bg-green-500 hover:bg-green-600 text-white">
                          <MessageCircle className="w-4 h-4 mr-1" />
                          Order
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/medicines">
              <Button size="lg" variant="outline" className="group">
                View All Health Kits
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </AnimatedSection>

      {/* Why Choose Us */}
      <AnimatedSection className="py-24 bg-primary/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl sm:text-5xl font-bold font-serif">Why Choose Aatreya Herbals</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Trusted by thousands for authentic Ayurvedic care
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: '100% Natural',
                description: 'Pure herbal ingredients with no artificial additives or chemicals'
              },
              {
                icon: Heart,
                title: 'Expert Care',
                description: 'Certified Ayurvedic practitioners with years of experience'
              },
              {
                icon: Sparkles,
                title: 'Proven Results',
                description: 'Time-tested formulations backed by ancient wisdom'
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                whileHover={{ y: -5 }}
                className="bg-card rounded-2xl p-8 text-center border border-border shadow-lg hover:shadow-xl transition-all"
              >
                <motion.div
                  whileHover={{ rotate: 360, scale: 1.1 }}
                  transition={{ duration: 0.6 }}
                  className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6"
                >
                  <feature.icon className="w-8 h-8 text-primary" />
                </motion.div>
                <h3 className="font-serif font-semibold text-xl mb-3">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Testimonials Marquee */}
      <AnimatedSection className="py-24 bg-primary/5 overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <div className="text-center space-y-4">
            <h2 className="text-4xl sm:text-5xl font-bold font-serif">What Our Patients Say</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Real stories from real people
            </p>
          </div>
        </div>

        <div className="relative">
          <div className="flex space-x-6 animate-marquee">
            {[...mockTestimonials, ...mockTestimonials].map((testimonial, index) => (
              <div
                key={`${testimonial.id}-${index}`}
                className="flex-shrink-0 w-96 bg-card rounded-2xl p-8 border border-border shadow-lg"
              >
                <div className="flex items-center space-x-4 mb-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <h4 className="font-semibold">{testimonial.name}</h4>
                    <p className="text-sm text-muted-foreground">{testimonial.location}</p>
                  </div>
                </div>
                <div className="flex mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Sparkles key={i} className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
                <p className="text-muted-foreground italic">"{testimonial.text}"</p>
                <p className="text-sm text-primary mt-3">Treatment: {testimonial.treatment}</p>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* CTA Section */}
      <AnimatedSection className="py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative bg-primary rounded-3xl overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
                className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"
              />
            </div>
            <div className="relative z-10 px-8 py-16 md:px-16 md:py-24 text-center space-y-6">
              <h2 className="text-4xl sm:text-5xl font-bold font-serif text-primary-foreground">
                Begin Your Wellness Journey Today
              </h2>
              <p className="text-primary-foreground/90 text-lg max-w-2xl mx-auto">
                Book a consultation with our expert practitioners and discover personalized Ayurvedic solutions for your health goals.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <Button
                  size="lg"
                  variant="secondary"
                  className="group bg-green-500 hover:bg-green-600 text-white"
                  onClick={() => window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hello Aatreya Herbals! I want to book a free consultation with a doctor.')}`, '_blank')}
                  data-testid="home-book-free-consultation-whatsapp"
                >
                  <MessageCircle className="mr-2 w-5 h-5" />
                  Book Free Consultation
                </Button>
                <Link to="/practitioners">
                  <Button size="lg" variant="outline" className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                    Meet Our Practitioners
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>
    </div>
  );
};

export default Home;